import { useEffect, useState } from 'react';
// import env from '../environment';
import { useCoopConfig } from '../components/content/coop-config';
import toDegreesMinutesAndSeconds from '../utils/toDegreesMinutesAndSeconds';
import parsePoint from '../utils/parsePoint';
import getZoomForArea from '../utils/getZoomForArea';
import eliminarPuntosDuplicados from '../utils/eliminarPuntosDuplicados';
import calculateAngle from '../utils/calculateAngle';

const useCroquisHook = ({ parcelaPolygon, centroid, poligonoAltitudes = null }, color = null, fetchElevationDataOnStart = false) => {
  const { mostrarAreaCalculada } = useCoopConfig();
  const shouldShowAreaCalculada = mostrarAreaCalculada();
  const [center, setCenter] = useState({ lat: 16, lng: -92 });
  const [angle, setAngle] = useState(0);
  const [areaGMaps, setAreaGMaps] = useState(null);
  const [zoom, setZoom] = useState(17);
  const [centroidProcessed, setCentroidProcessed] = useState({
    dec: { lat: 0, lng: 0 },
    deg: { lat: 0, lng: 0 },
    elevation: null,
  });
  const [pointsOnMap, setPointsOnMap] = useState([]);
  const [pointsOnMapDegrees, setPointsOnMapDegrees] = useState([]);
  const [lengths, setLengths] = useState([]);
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const [hasPreloadedElevations, setHasPreloadedElevations] = useState(false);

  useEffect(() => {
    let currentPolygon = null;
    const getMapData = async () => {
      // Enhanced loading guards - ensure all required dependencies are available
      if (map != null && maps != null && maps.geometry && parcelaPolygon != null) {
        try {
          const poligono = JSON.parse(parcelaPolygon);
          const poligono_data = poligono.coordinates[0].map(function(p) {
            return { lat: p[1], lng: p[0] };
          });

          currentPolygon = new maps.Polygon({
            paths: poligono_data,
            strokeColor: color ?? '#91B833',
            strokeOpacity: 1,
            strokeWeight: color ? 4 : 2,
            fillColor: color ?? '#f2f1e8',
            fillOpacity: 0.5,
            // editable: env.appEnv === 'local' || env.appEnv === 'dev',
            // draggable: env.appEnv === 'local' || env.appEnv === 'dev',
          });
          currentPolygon.setMap(map);
          if (poligono && poligono.coordinates && poligono.coordinates[0] && poligono.coordinates[0][0] && poligono.coordinates[0][0][1] && poligono.coordinates[0][0][0]) {
            const latCenter = parseFloat(poligono.coordinates[0][0][1]);
            const lngCenter = parseFloat(poligono.coordinates[0][0][0]);
            setCenter({ lat: latCenter, lng: lngCenter });
          } else if (centroid && centroid.lat && centroid.lng) {
            setCenter({ lat: centroid.lat, lng: centroid.lng });
          }

          // Google api area y convertir a Hectáreas
          const area = poligono_data ? maps.geometry.spherical.computeArea(poligono_data) / 10000 : 0;
          setAreaGMaps(area?.toFixed(2));
          setZoom(getZoomForArea(area));

          // Distancia entre cada punto del polígono
          // ubicado entre los dos puntos
          const lengthsTemp = [];
          if (shouldShowAreaCalculada) {
            for (let i = 1; i < poligono_data.length; i++) {
              const curr = poligono_data[i];
              const prev = poligono_data[i - 1];
              const dist = maps.geometry.spherical.computeDistanceBetween(curr, prev);
              // No mostrar distancias muy pequeñas porque se enciman en el mapa
              if (dist > 4) {
                var midPoint = new maps.Point((curr.lat + prev.lat) / 2, (curr.lng + prev.lng) / 2);
                lengthsTemp.push({
                  lat: midPoint.x,
                  lng: midPoint.y,
                  length: dist.toFixed(),
                });
              }
            }
          }
          setLengths(lengthsTemp);

          const poligono_data_filtrado = eliminarPuntosDuplicados(poligono_data);
          const usePreloaded =
            Array.isArray(poligonoAltitudes) && poligonoAltitudes.length === poligono_data_filtrado.length;
          let elevationData = [];
          let elevator = null;
          if (fetchElevationDataOnStart && !usePreloaded) {
            elevator = new maps.ElevationService();
            const response = await elevator.getElevationForLocations({ locations: poligono_data_filtrado }).catch(error => {
              // eslint-disable-next-line no-console
              console.log('error', error);
            });
            elevationData = response.results;
          }
          const points = [];
          poligono_data_filtrado.forEach((pnt, i) => {
            points.push({
              lat: pnt.lat,
              lng: pnt.lng,
              elevation: usePreloaded
                ? poligonoAltitudes[i]
                : elevationData.length > 0
                ? elevationData[i].elevation.toFixed()
                : null,
              title: getAlphaOrder(i, poligono_data_filtrado.length),
            });
          });
          setHasPreloadedElevations(usePreloaded);
          if (fetchElevationDataOnStart || usePreloaded) {
            findInclinationOfLandscape(points);
          }
          setPointsOnMap(points);
          const pointsDegrees = points.map(pnt => {
            return {
              ...pnt,
              lat: toDegreesMinutesAndSeconds(pnt.lat, true),
              lng: toDegreesMinutesAndSeconds(pnt.lng),
            };
          });
          setPointsOnMapDegrees(pointsDegrees);
          const precomputedCentroidElevation = usePreloaded
            ? (
                poligonoAltitudes.reduce((sum, v) => sum + parseFloat(v), 0) / poligonoAltitudes.length
              ).toFixed()
            : null;
          processCentroid(elevator, centroid.lat, centroid.lng, precomputedCentroidElevation);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating polygon:', error);
        }
      } else if (map != null && maps != null && centroid && centroid.lat && centroid.lng) {
        // Handle case where we only have centroid data (no polygon)
        const elevator = fetchElevationDataOnStart ? new maps.ElevationService() : null;
        processCentroid(elevator, centroid.lat, centroid.lng);
      }
    };
    getMapData();

    // Cleanup function to remove polygon when dependencies change
    return () => {
      if (currentPolygon) {
        currentPolygon.setMap(null);
      }
    };
  }, [map, maps, parcelaPolygon, centroid, color, shouldShowAreaCalculada, poligonoAltitudes]);

  const renderMap = (map, maps) => {
    setMap(map);
    setMaps(maps);
  };

  const findInclinationOfLandscape = points => {
    // Find Min and Max points for elevation direction
    const min = points.reduce((prev, curr) => (parseFloat(prev.elevation) < parseFloat(curr.elevation) ? prev : curr));
    const max = points.reduce((prev, curr) => (parseFloat(prev.elevation) < parseFloat(curr.elevation) ? curr : prev));
    setAngle(calculateAngle(max.lat, max.lng, min.lat, min.lng));
  };

  const getElevations = async () => {
    if (maps) {
      try {
        const elevator = new maps.ElevationService();
        if (centroidProcessed.dec) {
          const response = await elevator
            .getElevationForLocations({ locations: [{ lat: Number(centroidProcessed.dec.lat), lng: Number(centroidProcessed.dec.lng) }] })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.log('error', error);
            });

          let elevCentroid = 0;
          if (response && response.results?.length > 0 && !isNaN(response.results[0].elevation)) {
            elevCentroid = response.results[0].elevation.toFixed();
          }
          setCentroidProcessed({
            dec: centroidProcessed.dec,
            deg: centroidProcessed.deg,
            elevation: elevCentroid,
          });
        }
        if (pointsOnMap) {
          const responsePoints = await elevator
            .getElevationForLocations({ locations: pointsOnMap.map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })) })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.log('error', error);
            });
          const updatedPointsOnMap = pointsOnMap.map((p, i) => ({ ...p, elevation: responsePoints.results[i].elevation.toFixed() }));
          setPointsOnMap(updatedPointsOnMap);
          findInclinationOfLandscape(updatedPointsOnMap);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error calculating elevations:', error);
      }
    }
    return null;
  };

  const processCentroid = async (elevator = null, lat, lng, precomputedElevation = null) => {
    let elevCentroid = null;
    if (precomputedElevation !== null) {
      elevCentroid = precomputedElevation;
    } else if (elevator) {
      const response = await elevator.getElevationForLocations({ locations: [{ lat: lat, lng: lng }] }).catch(error => {
        // eslint-disable-next-line no-console
        console.log('error', error);
      });
      if (response && response.results?.length > 0 && !isNaN(response.results[0].elevation)) {
        elevCentroid = response.results[0].elevation.toFixed();
      }
    }
    setCentroidProcessed({
      dec: {
        lat: parsePoint(lat),
        lng: parsePoint(lng),
      },
      deg: {
        lat: toDegreesMinutesAndSeconds(lat, true),
        lng: toDegreesMinutesAndSeconds(lng),
      },
      elevation: elevCentroid,
    });
  };

  // Listar A, B, C o listar 0A, 0B en caso de ser número más grande
  const getAlphaOrder = (n, total) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    if (total >= alphabet.length) {
      return Math.floor(0 + n / alphabet.length).toString() + alphabet[n % alphabet.length];
    }
    return alphabet[n];
  };

  return {
    areaGMaps,
    renderMap,
    center,
    angle,
    pointsOnMap,
    pointsOnMapDegrees,
    centroidProcessed,
    lengths,
    zoom,
    getElevations,
    map,
    maps,
    hasPreloadedElevations,
  };
};

export default useCroquisHook;
